import React, { useState } from 'react';
import ReactDOM from 'react-dom';

import { format } from 'date-fns';
import { Formik } from 'formik';
import * as Yup from 'yup';

import BudgetByDimensionReportTable from './BudgetByDimensionReportTable';
import { BudgetByDimensionReportTableProvider } from './context/BudgetByDimensionReportTableContext';

import { getDateFnsLocale } from 'Helpers/localeService';
import { apiClient } from 'Helpers/network';

import Alert from 'CommonComponents/Alert';
import Button from 'CommonComponents/Button';

import { ToastProvider } from 'Context/ToastContext';

import useQuery from 'Hooks/useQuery';
import { useToast } from 'Hooks/useToast';

const ValidationSchema = Yup.object().shape({
    comments: Yup.object().test('comments-max-length', $t('Each comment must be at most 1000 characters'), (obj) => {
        if (!obj) return true;
        return Object.values(obj).every((v) => !v || v.length <= 1000);
    }),
});

/**
 * Creator-facing online-review page for Budgets by Venue / Budgets by Event.
 *
 * Mirrors the departments DepartmentsBudgetsReview flow: it fetches the frozen
 * snapshot (which carries its own dimensions / report_type / include_* criteria),
 * shows the reviewer's comments read-only, and — in edit mode — lets the creator
 * add their own per-line comments and re-send the review to the reviewer.
 */
const DimensionBudgetReview = ({ reviewDataUrl, resendUrl, archiveUrl, editMode: initialEditMode, dimension }) => {
    const [editMode, setEditMode] = useState(initialEditMode);

    const toast = useToast();

    const reviewDataRequest = useQuery({
        queryFn: () => apiClient.get(reviewDataUrl),
    });

    const data = reviewDataRequest.data;
    const report = data?.report;

    const getStatusLabel = () => {
        if (data?.status === 'reviewing') return $t('Under Review');
        if (data?.has_been_reviewed) return $t('Reviewed');
        return $t('Awaiting Review');
    };

    return (
        <Formik
            enableReinitialize
            initialValues={{ comments: data?.flat_comments || {} }}
            validationSchema={ValidationSchema}
            onSubmit={(values, { setSubmitting }) => {
                if (!resendUrl) {
                    setSubmitting(false);
                    return;
                }
                apiClient
                    .post(resendUrl, { comments: values.comments })
                    .then(() => {
                        toast.success($t('Review re-sent successfully.'));
                        if (archiveUrl) {
                            window.location.href = archiveUrl;
                        }
                    })
                    .catch(() => {
                        toast.error($t('Failed to re-send review. Please try again.'));
                    })
                    .finally(() => {
                        setSubmitting(false);
                    });
            }}
        >
            {({ values, resetForm, handleSubmit, isSubmitting }) => (
                <div className="tw-h-full tw-flex tw-flex-col tw-space-y-6 tw-py-12">
                    {editMode && (
                        <Alert type="info">
                            {$t('You are in edit mode. Modify the comments below and click "Re-Send" to send the updated review back to the reviewer.')}
                        </Alert>
                    )}

                    {!reviewDataRequest.loading && !data?.has_been_reviewed && <Alert type="warning">{$t('This budget is not yet reviewed.')}</Alert>}

                    <BudgetByDimensionReportTableProvider
                        criteria={report?.criteria}
                        report={report}
                        dimensions={report?.dimensions}
                        dimension={report?.dimension || dimension}
                        comments={values.comments}
                        allComments={data?.comments || {}}
                        userType={data?.user_type || 'creator'}
                        loading={reviewDataRequest.loading}
                        showComments
                        editableComments={editMode}
                    >
                        <BudgetByDimensionReportTable />
                    </BudgetByDimensionReportTableProvider>

                    <div className="tw-flex tw-flex-col tw-gap-4 md:tw-flex-row md:tw-items-start md:tw-justify-between tw-pb-8">
                        <table className="tw-max-w-md">
                            <tbody>
                                <tr>
                                    <td className="tw-font-medium tw-pr-4">{$t('Status')}:</td>
                                    <td>{getStatusLabel()}</td>
                                </tr>
                                <tr>
                                    <td className="tw-font-medium tw-pr-4">{$t('Reviewer Email')}:</td>
                                    <td>{data?.reviewer_email || '-'}</td>
                                </tr>
                                <tr>
                                    <td className="tw-font-medium tw-pr-4">{$t('Reviewer Name')}:</td>
                                    <td>{data?.reviewer_name || '-'}</td>
                                </tr>
                                {data?.reviewed_at && (
                                    <tr>
                                        <td className="tw-font-medium tw-pr-4">{$t('Reviewed At')}:</td>
                                        <td>{format(new Date(data.reviewed_at), 'PPpp', { locale: getDateFnsLocale() })}</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>

                        {editMode && resendUrl && (
                            <div className="tw-flex tw-gap-3">
                                <Button
                                    type="button"
                                    variant="secondary"
                                    onClick={() => {
                                        setEditMode(false);
                                        resetForm({ values: { comments: data?.flat_comments || {} } });
                                    }}
                                    disabled={isSubmitting}
                                >
                                    {$t('Cancel')}
                                </Button>
                                <Button type="button" variant="primary" onClick={handleSubmit} loading={isSubmitting} disabled={isSubmitting}>
                                    {$t('Re-Send')}
                                </Button>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </Formik>
    );
};

export default DimensionBudgetReview;

if (document.getElementById('DimensionBudgetReview')) {
    const rootEl = document.getElementById('DimensionBudgetReview');

    const reviewDataUrl = rootEl.getAttribute('data-review-data-url');
    const resendUrl = rootEl.getAttribute('data-resend-url');
    const archiveUrl = rootEl.getAttribute('data-archive-url');
    const editMode = rootEl.getAttribute('data-edit-mode') === 'true';
    const dimension = rootEl.getAttribute('data-dimension') || 'venue';

    ReactDOM.render(
        <ToastProvider>
            <DimensionBudgetReview reviewDataUrl={reviewDataUrl} resendUrl={resendUrl} archiveUrl={archiveUrl} editMode={editMode} dimension={dimension} />
        </ToastProvider>,
        rootEl,
    );
}
